import React from 'react';
import styled from 'styled-components/macro';

import { colors, distances, zIndex } from '../../styles/constants';
import Icon from '../Icons';
import { Wrapper, Button, Menu, MenuItem } from 'react-aria-menubutton';

import { Label } from '../Typography';

export interface DropdownOption {
    label: any;
    value: string;
}

export type OnChangeFunction = (event: Event) => void;

interface DropdownProps {
    name: string;
    label?: any;
    value: DropdownOption;
    onChange: (option: DropdownOption) => void;
    disabled?: boolean;
    options: DropdownOption[];
    className?: string;
    required?: boolean;
    selectedFormatter?: (option: DropdownOption) => string;
}

const Dropdown = (
    {
        name,
        label,
        value,
        options,
        onChange,
        disabled,
        className,
        required,
        selectedFormatter,
    }: DropdownProps
) => {
    const SelectedComponent = value.value === '' ? SelectedPlaceholder : Selected;
    return (
        <StyledWrapper onSelection={onChange} className={className}>
            {label && <Label htmlFor={name}>{label}{required ? ' *' : ''}</Label>}
            <SelectedComponent id={name} name={name} disabled={disabled}>
                {selectedFormatter ? selectedFormatter(value) : value.label}
                <OffsetIcon icon="caret_down" isLabel={!!label} />
            </SelectedComponent>
            <Options>
                <>
                    {options.map((option) => (
                        <Option key={option.value} value={option}>
                            {option.label}
                        </Option>
                    ))}
                </>
            </Options>
        </StyledWrapper>
    );
};

const StyledWrapper = styled(Wrapper)`
    position: relative;
`;

const Selected = styled(Button)`
    border: none;
    outline: none;
    display: block;
    background: ${({ disabled }) => {
        // Seems like the &:disabled selector doesn't work
        // as expected with the react-aria-menubutton Button component. Atleast not in FF
        return disabled ? colors.backgroundAlt : colors.background;
    }};
    padding-top: 6px;
    padding-left: ${distances.tiny};
    padding-bottom: 1px;
    padding-right: 1px;
    width: 100%;
    min-height: 34px;

    margin-bottom: ${distances.small};
    color: ${colors.primary};
    font-weight: 700;
    border-bottom: 2px solid;
    border-color: ${colors.borderLight};
    cursor: pointer;
    text-align: left;

    .open {
        border-color: rgba(107, 198, 246, 1);
    }
    &:focus {
        outline: none;
        background: ${colors.interactiveBackgroundHover};
    }
    &:disabled {
        background: ${colors.backgroundAlt};
    }
`;

const SelectedPlaceholder = styled(Selected)`
    color: ${colors.borderDark};
    font-weight: 400;
`;

const OffsetIcon = styled(Icon)<{ isLabel: boolean }>`
    top: ${(props) => (props.isLabel ? '35px' : '7px')};
    position: absolute;
    right: 8px;
`;

const Options = styled(Menu)`
    width: 100%;
    list-style: none;
    margin: 0;
    padding: 0;
    position: absolute;
    top: 34px;
    z-index: ${zIndex.top};
    background-color: ${colors.background};
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    transition: transform 150ms ease-out;
    transform-origin: top center;
`;

const Option = styled(MenuItem)`
    margin: 0;
    padding: ${distances.tiny} 16px;
    white-space: nowrap;
    cursor: pointer;

    &:focus {
        background: ${colors.interactiveBackgroundHover};
        outline: 1px solid ${colors.primary};
        outline-offset: 0;
    }

    &:hover {
        background-color: ${colors.interactiveBackgroundHover};
    }
`;

export default Dropdown;
